import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import {
  MediaProductsResponse,
  OneProductResponse,
  ProductReviewsResponse,
  ProductsAndFiltersResponse,
  ProductsAutocompleteResponse,
  ProductSectionResponse,
  ProductsListResponse,
  ProductsResponse,
  ProductsSearchListResponse,
  ProductVariant,
  SubmitResponse
} from '../../models';
import { BulkPurchaseRequest, ProductInput, productQuickUpdateInput, productQuickUpdateStockInput, ProductSearchInput } from '../../inputs/product.input';
import { Router } from '@angular/router';

export type ProductDetailSection = 'main' | 'used' | 'bazaar';

@Injectable({
  providedIn: 'root'
})
export class ProductService extends HttpService<
  ProductInput | ProductSearchInput | BulkPurchaseRequest | productQuickUpdateInput | productQuickUpdateStockInput,
  ProductVariant | ProductsResponse | OneProductResponse | ProductReviewsResponse | ProductsAndFiltersResponse |
  ProductSectionResponse | MediaProductsResponse | ProductsListResponse | ProductsSearchListResponse |
  ProductsAutocompleteResponse | SubmitResponse | any
> {
  constructor(
    httpClient: HttpClient,
    @Inject('API_URL') apiUrl: string,
    @Inject('API_VERSION') apiVersion: string,
    private router: Router
  ) {
    super(httpClient, apiUrl, apiVersion, 'product-variant');
  }

  getAllProducts(page = 1): Observable<ProductsResponse> {
    return this.getAllPaged('list', page) as Observable<ProductsResponse>;
  }

  getProductsByCategory(page = 1, categoryId: number): Observable<ProductsAndFiltersResponse> {
    return this.getAllPaged(`category/${categoryId}`, page) as Observable<ProductsAndFiltersResponse>;
  }

  getFilteredProductsByCategory(page = 1, categoryId: number, filters: string): Observable<ProductsAndFiltersResponse> {
    return this.getAll(`category/${categoryId}/filter?page=${page}&${filters}`) as Observable<ProductsAndFiltersResponse>;
  }

  getProductsForMedia(page = 1): Observable<MediaProductsResponse> {
    return this.getAllPaged(`media`, page) as Observable<MediaProductsResponse>;
  }

  getProductReviews(productId: number): Observable<ProductReviewsResponse> {
    return this.getById(`${productId}/reviews`) as Observable<ProductReviewsResponse>;
  }

  getProductById(id: number): Observable<ProductVariant> {
    return this.getById(id) as Observable<ProductVariant>;
  }

  getProductDetailById(productId: number): Observable<OneProductResponse> {
    return this.getById(`details/${productId}`) as Observable<OneProductResponse>;
  }

  getProductsBySection(sectionId: number, page = 1): Observable<ProductSectionResponse> {
    return this.getAllPaged(`section/${sectionId}`, page, 'productSection') as Observable<ProductSectionResponse>;
  }

  createProduct(product: ProductInput): Observable<SubmitResponse> {
    return this.create(product, 'create');
  }

  updateProduct(product: ProductInput): Observable<SubmitResponse> {
    return this.update(product);
  }

  deleteProduct(productId: number): Observable<SubmitResponse> {
    return this.delete(productId);
  }
  // new
  getAllProductsVarients(page = 1): Observable<ProductsListResponse> {
    return this.getAllPaged('?limit=30&', page) as Observable<ProductsListResponse>;
  }

  searchVariants(input: ProductSearchInput): Observable<ProductsSearchListResponse> {
    return this.searchAllPaged(input) as Observable<ProductsSearchListResponse>;
  }

  searchVariantsAdmin(input: ProductSearchInput): Observable<ProductsSearchListResponse> {
    return this.httpClient
      .post<any>(`${this.apiUrl}/admin/search`, input)
      .pipe(map((response) => response.data as ProductsSearchListResponse))
      .pipe(catchError((error) => throwError(() => error)));
  }

  // searchAutoComplete(query: string): Observable<ProductsAutocompleteResponse> {
  //   return this.searchAllPaged(input) as Observable<ProductsAutocompleteResponse>;
  // }

  getVariant(id: number, section?: ProductDetailSection): Observable<ProductVariant> {
    return this.getById(section ? `${id}?section=${section}` : id).pipe(
      catchError(error => {
        // Scoped detail pages handle all 404s with their localized navigation.
        // Keep the existing behavior for unscoped callers such as reviews.
        if (!section && error.status === 404 && error.error?.error_code !== 'USED_SECTION_DISABLED') {
          this.router.navigate(['/404']);
        }
        return throwError(() => error);
      })
    );
  }

  getAllVariants(page: number, limit?: number): Observable<ProductsListResponse> {
    return this.getAllPaged('', page, '', limit) as Observable<ProductsListResponse>;
  }

  getAllByTag(page: number, tagIdOrSlug = 'new-arrival', limit?: number): Observable<ProductsListResponse> {
    return this.getAllPaged(`tag/${tagIdOrSlug}/`, page, tagIdOrSlug, limit) as Observable<ProductsListResponse>;
  }
  getAllByTagForSections(id: number): Observable<ProductsListResponse> {
    return this.getAllPaged(`tag/${id}/?page=1&limit=300&unpaginated=true`) as Observable<ProductsListResponse>;
  }


  getVideoVariants(page: number): Observable<ProductsListResponse> {
    return this.getAllPaged('videos', page) as Observable<ProductsListResponse>;
  }
  getProductVariantsByProductId(productId: string): Observable<ProductsListResponse> {
    return this.getAll('product/' + productId) as Observable<ProductsListResponse>;
  }
  getAllNewArrivalProducts(page = 1, limit = 30, lastDays = 100): Observable<ProductsListResponse> {
    return this.getAllNPaged('new-arrival', page, `last_days=${lastDays}`, limit) as Observable<ProductsListResponse>;
  }
  bulkQuotation(bulkInput: BulkPurchaseRequest): Observable<ProductsListResponse> {
    // Re-throw so the caller's error handler runs. (Was `of(error)`, which turned every
    // failure into a "success" emission — the modal then showed a false confirmation.)
    return this.createQuatation(bulkInput, `bulk-quotation-request`).pipe(catchError((error) => throwError(() => error))) as Observable<ProductsListResponse>;
  }

  notifyBackInStock(productId: number, form: any): Observable<SubmitResponse> {
    return this.create(form, `${productId}/in-stock-notification`)
  }
  getAllCategoryProducts(brandSlug:string,catSlug:string,page = 1, limit = 30, lastDays = 100): Observable<ProductsListResponse> {
  return this.getAllNPaged('brand/'+brandSlug+'/category/'+catSlug, page, `last_days=${lastDays}`, limit) as Observable<ProductsListResponse>;
}

  updateProductPrice(productId: string, product: productQuickUpdateInput): Observable<SubmitResponse> {
    return this.update(product, `${productId}/price`);
  }
  updateProductStock(productId: string, product: productQuickUpdateStockInput): Observable<SubmitResponse> {
    return this.update(product, `${productId}/stock`);
  }

}
